/**
 * useProfileImageUpload hook
 * Manages the three-step profile image upload flow:
 * 1. Get presigned S3 POST URL from backend
 * 2. Upload image directly to S3 using multipart/form-data POST
 * 3. Confirm upload with backend to store URL in user profile
 *
 * Uses TanStack Query useMutation for state management
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getUploadUrl, confirmUpload } from '@/services/profileImage';
import type { AuthError } from '@/types/auth';

/** Input for the upload mutation */
export interface UploadInput {
  /** The image File object from the file picker */
  file: File;
}

/** Return type for useProfileImageUpload hook */
export interface UseProfileImageUploadReturn {
  /** Function to trigger the upload flow with a selected file */
  uploadImage: (input: UploadInput) => void;
  /** True while any step of the upload flow is in progress */
  isUploading: boolean;
  /** Error object if any step failed */
  error: AuthError | null;
  /** True after the entire three-step flow completes successfully */
  isSuccess: boolean;
  /** The URL of the successfully uploaded image */
  uploadedImageUrl: string | null;
  /** Reset mutation state (clear errors, success) */
  reset: () => void;
}

/** Allowed MIME types for profile images */
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Maximum file size in bytes (2MB) */
const MAX_FILE_SIZE = 2 * 1024 * 1024;

/**
 * Hook to upload a profile image using the three-step presigned POST flow
 *
 * @returns Object with uploadImage function and state flags
 *
 * @example
 * ```typescript
 * const { uploadImage, isUploading, error, isSuccess } = useProfileImageUpload();
 *
 * const handleFileSelect = (file: File) => {
 *   uploadImage({ file });
 * };
 * ```
 */
export function useProfileImageUpload(): UseProfileImageUploadReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    string, // Success type: the uploaded image URL
    AuthError, // Error type
    UploadInput // Variables type
  >({
    mutationFn: async ({ file }: UploadInput) => {
      // --- Client-side validation ---
      if (!ALLOWED_TYPES.includes(file.type)) {
        throw {
          code: 'VALIDATION_ERROR',
          message: 'Invalid file type. Please upload a JPEG, PNG, or WebP image.',
        } as AuthError;
      }

      if (file.size > MAX_FILE_SIZE) {
        throw {
          code: 'VALIDATION_ERROR',
          message: 'File is too large. Maximum size is 2MB.',
        } as AuthError;
      }

      // --- Step 1: Get presigned upload URL from backend ---
      const { url, fields, imageKey } = await getUploadUrl(file.type);

      // --- Step 2: Upload image directly to S3 using presigned POST (multipart/form-data) ---
      try {
        // Build FormData: all presigned POST fields MUST come before the file
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        // File must be appended last for S3 presigned POST to work correctly
        formData.append('file', file);

        // Do NOT set Content-Type header — browser auto-sets multipart/form-data with boundary
        const uploadResponse = await fetch(url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
        }
      } catch (err) {
        // Wrap network/S3 errors in the standard error format
        throw {
          code: 'NETWORK_ERROR',
          message: 'Upload failed. Please check your connection and try again.',
        } as AuthError;
      }

      // --- Step 3: Confirm upload with backend ---
      const { profileImageUrl } = await confirmUpload(imageKey);

      return profileImageUrl;
    },

    onSuccess: () => {
      // Invalidate user profile cache so all components get the updated profileImageUrl
      queryClient.invalidateQueries({
        queryKey: ['user', 'profile'],
      });
    },
  });

  return {
    uploadImage: (input: UploadInput) => {
      mutation.mutate(input);
    },
    isUploading: mutation.isPending,
    error: mutation.error ?? null,
    isSuccess: mutation.isSuccess,
    uploadedImageUrl: mutation.data ?? null,
    reset: mutation.reset,
  };
}
